// Extension modules in the browser (PLAN §7.2). The server says which modules the organisation has
// enabled (/me/modules); only those are downloaded. Every contribution renders inside an error boundary,
// so a broken module shows a small notice instead of breaking the page around it.
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import type { SlotName, SlotProps, WebModule, WebModuleContext } from '@tatra/sdk/web';
import { Card, toast } from '@tatra/ui';
import { Component, createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { api, get } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { WEB_MODULES } from './installed.generated.js';

interface EnabledModule {
  key: string;
  name: string;
  version: string;
}

interface LoadedModule {
  info: EnabledModule;
  web: WebModule;
  ctx: WebModuleContext;
}

interface ExtensionsState {
  modules: LoadedModule[];
  loading: boolean;
}

const ExtensionsContext = createContext<ExtensionsState>({ modules: [], loading: false });

function moduleApi(key: string): WebModuleContext['api'] {
  const base = `/m/${key}`;
  return {
    get: (p) => api('GET', `${base}${p}`),
    post: (p, b) => api('POST', `${base}${p}`, b ?? {}),
    put: (p, b) => api('PUT', `${base}${p}`, b ?? {}),
    patch: (p, b) => api('PATCH', `${base}${p}`, b ?? {}),
    del: (p) => api('DELETE', `${base}${p}`),
  };
}

export function ExtensionsProvider({ children }: { children: ReactNode }) {
  const { status, can } = useAuth();
  const navigate = useNavigate();
  const enabled = useQuery({
    queryKey: ['me-modules'],
    queryFn: () => get<EnabledModule[]>('/me/modules'),
    enabled: status === 'signed_in',
    staleTime: 60_000,
  });
  const [loaded, setLoaded] = useState<{ info: EnabledModule; web: WebModule }[]>([]);

  useEffect(() => {
    let cancelled = false;
    const list = (enabled.data ?? []).filter((m) => WEB_MODULES[m.key]);
    void Promise.all(
      list.map(async (info) => {
        try {
          const web = (await WEB_MODULES[info.key]!()).default;
          return web.key === info.key ? { info, web } : null;
        } catch (err) {
          console.error(`module ${info.key} failed to load`, err);
          return null;
        }
      }),
    ).then((mods) => {
      if (!cancelled) setLoaded(mods.filter((m): m is { info: EnabledModule; web: WebModule } => m !== null));
    });
    return () => {
      cancelled = true;
    };
  }, [enabled.data]);

  const value = useMemo<ExtensionsState>(
    () => ({
      loading: enabled.isPending && status === 'signed_in',
      modules: loaded.map(({ info, web }) => ({
        info,
        web,
        ctx: {
          key: info.key,
          api: moduleApi(info.key),
          can,
          navigate: (to) => void navigate({ to }),
          toast: { success: (m) => toast.success(m), error: (m) => toast.error(m) },
        },
      })),
    }),
    [loaded, can, navigate, enabled.isPending, status],
  );
  return <ExtensionsContext.Provider value={value}>{children}</ExtensionsContext.Provider>;
}

export function useExtensions(): ExtensionsState {
  return useContext(ExtensionsContext);
}

/** Catches render errors of one module contribution. */
export class ModuleBoundary extends Component<
  { module: string; children: ReactNode; compact?: boolean },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error) {
    console.error(`module ${this.props.module} crashed`, error);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <Card
        role="alert"
        className={this.props.compact ? 'p-3 text-xs text-text-muted' : 'p-5 text-sm text-text-muted'}
      >
        The “{this.props.module}” module hit a problem here. The rest of the page still works.
      </Card>
    );
  }
}

/** Renders every enabled module's contributions to a named slot (each in its own boundary). */
export function Slot<K extends SlotName>({
  name,
  props,
  className,
}: {
  name: K;
  props: SlotProps[K];
  className?: string;
}) {
  const { modules } = useExtensions();
  const { can } = useAuth();
  const items = modules.flatMap((m) =>
    (m.web.slots?.[name] ?? []).filter((c) => !c.permission || can(c.permission)).map((c) => ({ m, c })),
  );
  if (items.length === 0) return null;
  return (
    <div className={className}>
      {items.map(({ m, c }) => {
        const Contribution = c.component as unknown as (p: Record<string, unknown>) => ReactNode;
        return (
          <ModuleBoundary key={`${m.info.key}:${c.id}`} module={m.info.name} compact>
            <Contribution ctx={m.ctx} {...(props as Record<string, unknown>)} />
          </ModuleBoundary>
        );
      })}
    </div>
  );
}

/** Matches `/a/:b` patterns against a path relative to `/m/<key>`. */
export function matchModuleRoute(pattern: string, path: string): Record<string, string> | null {
  const a = pattern.split('/').filter(Boolean);
  const b = path.split('/').filter(Boolean);
  if (a.length !== b.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.startsWith(':')) params[a[i]!.slice(1)] = decodeURIComponent(b[i]!);
    else if (a[i] !== b[i]) return null;
  }
  return params;
}
